import React, { useState, useRef, useContext, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Box, Typography, Container } from "@mui/material";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import MenteeHeader from "../../../components/MenteeHeader";
import BackButton from "../../../components/BackButton";
import { EventFormContext } from "../../../context/EventFormContext";
import DashboardFooter from "../../../components/Footer";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Defined at module scope so their object identity is stable across renders.
// Passing a fresh inline `options` object to <Document> makes react-pdf treat the
// input as changed and reload — destroying the running pdfjs worker mid-render,
// which surfaces as "Cannot read properties of null (reading 'sendWithPromise')"
// from the page layers. Stable references prevent the reload race.
const PDF_OPTIONS = {
  cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
  cMapPacked: true,
};
const THUMBNAIL_OPTIONS = {};

const ViewPdf = () => {
  const location = useLocation();
  const { mode } = location.state || {};
  const { eventId } = location.state || "";
  const navigate = useNavigate();
  const { formData } = useContext(EventFormContext);
  const file = formData.resourceFileUrl;
  const [numPages, setNumPages] = useState(null);
  const [currentPageInView, setCurrentPageInView] = useState(1);

  const pageRefs = useRef([]);
  const thumbnailRefs = useRef([]);
  const scrollContainerRef = useRef(null);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    pageRefs.current = Array(numPages)
      .fill(null)
      .map(() => React.createRef());
    thumbnailRefs.current = Array(numPages)
      .fill(null)
      .map(() => React.createRef());
  };

  useEffect(() => {
    const updatePdfLinks = () => {
      const links = document.querySelectorAll(".annotationLayer a");
      links.forEach((link) => {
        const href = link.getAttribute("href");

        // Only modify real external URLs (http/https)
        if (href && /^https?:\/\//i.test(href)) {
          link.setAttribute("target", "_blank");
          link.setAttribute("rel", "noopener noreferrer");
        } else {
          // Keep internal PDF anchors (#page=3 etc.) working normally
          link.removeAttribute("target");
          link.removeAttribute("rel");
        }
      });
    };

    // Initial run
    updatePdfLinks();

    // Observe changes since react-pdf renders async
    const observer = new MutationObserver(updatePdfLinks);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [numPages]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || numPages === null) return;

    // --- Debounce function ---
    const debounce = (func, delay) => {
      let timeoutId;
      return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          func.apply(this, args);
        }, delay);
      };
    };

    // --- The logic to find the current page ---
    const findCurrentPage = () => {
      let bestMatch = { page: 1, distance: Infinity };
      const containerTop = scrollContainer.scrollTop;
      const containerMidpoint = containerTop + scrollContainer.clientHeight / 2;

      pageRefs.current.forEach((ref, index) => {
        if (ref.current) {
          const pageElement = ref.current;
          const pageMidpoint =
            pageElement.offsetTop + pageElement.offsetHeight / 2;
          const distance = Math.abs(containerMidpoint - pageMidpoint);

          if (distance < bestMatch.distance) {
            bestMatch = { page: index + 1, distance: distance };
          }
        }
      });

      // Only update state if the page has actually changed
      if (bestMatch.page !== currentPageInView) {
        setCurrentPageInView(bestMatch.page);
      }
    };

    const handleScroll = debounce(findCurrentPage, 150); // Debounce for 150ms

    scrollContainer.addEventListener("scroll", handleScroll);

    // Cleanup function
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, [numPages, currentPageInView]); // Re-attach if numPages changes

  // EFFECT 2: Scroll the active thumbnail into view whenever currentPageInView changes
  useEffect(() => {
    const activeThumbnail =
      thumbnailRefs.current[currentPageInView - 1]?.current;
    if (activeThumbnail) {
      activeThumbnail.scrollIntoView({
        behavior: "smooth",
        block: "center", // Scrolls the thumbnail to the center of its container
      });
    }
  }, [currentPageInView]);

  if (!file) {
    return mode === "edit" ? (
      <Navigate to={`/events/edit-event/${eventId}`} />
    ) : (
      <Navigate to="/events/create-event" />
    );
  }

  return (
    <Box>
      <MenteeHeader />
      <Box>
        <Container maxWidth="xl">
          <Box
            sx={{
              padding: "10px",
              marginBottom: "10px",
              display: "flex",
              justifyContent: {
                xs: "flex-start",
                md: "center",
              },
              alignItems: "center",
            }}
          >
            <BackButton
              leftNormal={true}
              margin={0}
              onBackStep={() => navigate(-1)}
            />
            <Typography
              variant="h5"
              sx={{
                color: "#2A4876",
                fontWeight: "bold",
                marginLeft: { xs: "30px", md: 0 },
              }}
            >
              {decodeURIComponent(formData?.eventTitle)}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              height: "calc(100vh - 200px)",
              border: "1px solid #e0e0e0",
            }}
          >
            {/* Left Sidebar - Thumbnails */}
            {window.innerWidth > 768 && (
              <Box
                sx={{
                  width: "20%",
                  minWidth: "200px",
                  overflowY: "auto",
                  borderRight: "1px solid #ccc",
                  backgroundColor: "#f5f5f5",
                  p: 1,
                }}
              >
                {numPages && (
                  <Document file={file} options={THUMBNAIL_OPTIONS}>
                    {Array.from(new Array(numPages), (_, index) => (
                      <Box
                        key={`thumb-${index}`}
                        ref={thumbnailRefs.current[index]}
                        sx={{
                          mb: 1,
                          border:
                            currentPageInView === index + 1
                              ? "3px solid #1976d2"
                              : "1px solid #ddd",
                          cursor: "pointer",
                          borderRadius: "4px",
                          transition: "border 0.2s ease-in-out",
                          "&:hover": { borderColor: "#2A4876" },
                        }}
                        onClick={() => {
                          pageRefs.current[index]?.current?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                        }}
                      >
                        <Page pageNumber={index + 1} width={180} />
                      </Box>
                    ))}
                  </Document>
                )}
              </Box>
            )}
            {/* Right Scrollable Pages */}
            <Box
              ref={scrollContainerRef}
              id="pdf-scroll-container"
              sx={{
                flexGrow: 1,
                p: 2,
                overflowY: "auto",
                overflowX: "hidden",
                backgroundColor: "#eef2f6",
              }}
            >
              <Document
                file={file}
                onLoadSuccess={onDocumentLoadSuccess}
                options={PDF_OPTIONS}
              >
                {Array.from(new Array(numPages), (_, index) => (
                  <div
                    key={`page-${index}`}
                    ref={pageRefs.current[index]}
                    style={{
                      marginBottom: "20px",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Page
                      pageNumber={index + 1}
                      width={Math.min(window.innerWidth * 0.65, 800)}
                      renderAnnotationLayer={true} // Crucial for links
                      renderInteractiveForms={true}
                    />
                  </div>
                ))}
              </Document>
            </Box>
          </Box>
          <DashboardFooter />
        </Container>
      </Box>
    </Box>
  );
};

export default ViewPdf;
